"use client";
import { useState } from "react";
import { Plus, Minus, Search, X } from "lucide-react";
import questions, { type Question } from "../data/contactQuestions";

export default function Contact() {
  const [activeIndex, setActiveIndex] = useState<number | undefined>();
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");

  function handleToggle(index: number) {
    if (activeIndex === index) {
      setActiveIndex(undefined);
    } else {
      setActiveIndex(index);
    }
  }

  function clearSearch() {
    setSearchTerm("");
  }

  // Get all unique categories
  const categories: string[] = [
    "all",
    ...Array.from(new Set(questions.map((faq: Question) => faq.category))),
  ];

  // Filter questions based on selected category and search term
  const filteredFaqs: Question[] = questions.filter((faq: Question) => {
    const matchesCategory =
      activeCategory === "all" || faq.category === activeCategory;
    const matchesSearch =
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 to-purple-100">
      {/* Header Section */}
      <div className="max-w-7xl mx-auto px-4 py-16 sm:py-24 lg:py-32 gap-12 items-center justify-center">
        {/* Content */}
        <div className="flex flex-col justify-center items-center text-center mt-16">
          <h1 className="text-5xl sm:text-6xl font-bold text-[#2b2b2b] mb-10">
            We'd Love to Hear from You
          </h1>

          <p className="text-lg text-[#2b2b2b] mb-4 max-w-2xl">
            If you have any questions or need support, feel free to reach out —
            we're here to help. Before getting in touch, be sure to browse our
            FAQs below, as we may have already answered your query.
          </p>
          <p className="text-base text-[#2b2b2b] mb-12 max-w-2xl">
            Still need assistance? Drop us an email at{" "}
            <a
              href="mailto:hello@anamvr.com"
              className="text-blue-600 underline hover:text-blue-800"
            >
              hello@anamvr.com
            </a>{" "}
            and we'll get back to you as soon as we can.
          </p>
        </div>

        {/* FAQ Section */}
        <div className="px-4 pb-24">
          {/* Search Bar */}
          <div className="mb-12 px-4 sm:px-6 md:px-12 lg:px-24 xl:px-48">
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 pl-4 flex items-center">
                <Search className="h-5 w-5 text-[#2b2b2b]" />
              </div>
              <input
                type="text"
                placeholder="Search your question"
                className="w-full pl-12 pr-12 py-4 text-base border border-gray-300 rounded-2xl bg-white focus:outline-none focus:border-gray-300 transition-colors duration-200"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-[#2b2b2b] hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>

            {/* Search Results Counter */}
            {searchTerm && (
              <div className="mt-3 text-center">
                <span className="text-sm text-gray-600">
                  {filteredFaqs.length} result
                  {filteredFaqs.length !== 1 ? "s" : ""} found
                </span>
              </div>
            )}
          </div>
          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {categories.map((category: string) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeCategory === category
                    ? "bg-[#7745b8] text-white"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                }`}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
          </div>

          {/* FAQ Results */}
          <div className="flex flex-col gap-6 px-4 sm:px-6 md:px-12 lg:px-24 xl:px-48">
            {filteredFaqs.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-[#2b2b2b] mb-4">
                  <Search className="h-12 w-12 mx-auto" />
                </div>
                <h3 className="text-xl font-medium text-[#2b2b2b] mb-2">
                  No results found
                </h3>
                <p className="text-[#525252]">
                  Try adjusting your search terms or browse different categories
                </p>
              </div>
            ) : (
              filteredFaqs.map((item: Question, index: number) => (
                <div
                  key={index}
                  className={`pb-6 ${index === 0 ? "border-t border-[#545454]/30 pt-6 " : ""}${
                    index !== filteredFaqs.length - 1
                      ? "border-b border-[#545454]/30"
                      : ""
                  } cursor-pointer`}
                  onClick={() => handleToggle(index)}
                >
                  <h3 className="md:text-2xl sm:text-xl text-lg flex items-center justify-between font-medium text-black">
                    {item.question}
                    {activeIndex === index ? (
                      <Minus size={20} className="text-lg text-gray-700" />
                    ) : (
                      <Plus size={20} className="text-lg text-gray-700" />
                    )}
                  </h3>
                  {activeIndex === index && (
                    <div className="mt-4">
                      <p
                        className="text-base sm:text-lg text-gray-700"
                        dangerouslySetInnerHTML={{ __html: item.answer }}
                      ></p>

                      {/* Related Questions */}
                      {item.relatedQuestions &&
                        item.relatedQuestions.length > 0 && (
                          <div className="mt-4">
                            <p className="font-medium text-sm text-gray-700">
                              Related Questions:
                            </p>
                            <ul className="mt-2 space-y-1">
                              {item.relatedQuestions.map(
                                (question: string, idx: number) => (
                                  <li
                                    key={idx}
                                    className="text-sm text-[#7745b8]"
                                  >
                                    {question}
                                  </li>
                                )
                              )}
                            </ul>
                          </div>
                        )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
